import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import WaterDamageOutlinedIcon from '@mui/icons-material/WaterDamageOutlined';
import * as d3 from 'd3';

const StormDrainEntityDrawer = ({ entityDetails, chartRef }) => {
  // Mock data for storm drain monitoring (ultrasonic water depth sensor)
  const stormData = {
    status: 'Normal', // Normal | Elevated | Flood Risk
    currentLevelCm: 22,
    thresholdCm: { elevated: 40, flood: 60 },
    nextInspectionAt: '2024-01-18T14:00:00Z',
    level7d: [
      { day: 'Day -6', cm: 18 },
      { day: 'Day -5', cm: 25 },
      { day: 'Day -4', cm: 21 },
      { day: 'Day -3', cm: 35 },
      { day: 'Day -2', cm: 28 },
      { day: 'Day -1', cm: 44 },
      { day: 'Today', cm: 22 }
    ]
  };

  useEffect(() => {
    if (chartRef.current && stormData.level7d.length > 0) {
      renderLevelChart();
    }
  }, [stormData]);

  const renderLevelChart = () => {
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = stormData.level7d;

    const xScale = d3.scaleBand()
      .domain(data.map(d => d.day))
      .range([0, chartWidth])
      .padding(0.2);

    const yMax = Math.max(d3.max(data, d => d.cm) || 0, stormData.thresholdCm.flood);
    const yScale = d3.scaleLinear()
      .domain([0, yMax + 10])
      .nice()
      .range([chartHeight, 0]);

    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '10px');

    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Water Level (cm)')
      .style('font-size', '12px');

    // Threshold lines
    const thresholds = [
      { value: stormData.thresholdCm.elevated, color: '#FFB300', label: 'Elevated' },
      { value: stormData.thresholdCm.flood, color: '#E53935', label: 'Flood Risk' }
    ];

    thresholds.forEach(t => {
      const y = yScale(t.value);
      svg.append('line')
        .attr('x1', 0)
        .attr('x2', chartWidth)
        .attr('y1', y)
        .attr('y2', y)
        .attr('stroke', t.color)
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,4');
      svg.append('text')
        .attr('x', chartWidth - 5)
        .attr('y', y - 4)
        .attr('text-anchor', 'end')
        .style('fill', t.color)
        .style('font-size', '10px')
        .text(`${t.label} (${t.value}cm)`);
    });

    // Bars
    const bars = svg.selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.day))
      .attr('y', d => yScale(d.cm))
      .attr('width', xScale.bandwidth())
      .attr('height', d => chartHeight - yScale(d.cm))
      .attr('fill', d => d.cm >= stormData.thresholdCm.flood ? '#E53935' : d.cm >= stormData.thresholdCm.elevated ? '#FFB300' : '#29B6F6')
      .style('cursor', 'pointer');

    // Tooltip
    const tooltip = d3.select(chartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none');

    bars.on('mouseover', function(event, d) {
      tooltip.transition().duration(200).style('opacity', 0.9);
      tooltip.html(`<strong>${d.day}</strong><br/>Level: ${d.cm}cm`)
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 28) + 'px');
    }).on('mouseout', function() {
      tooltip.transition().duration(300).style('opacity', 0);
    });

    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Storm Drain Water Level - Last 7 Days');
  };

  return (
    <>
      {/* Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <WaterDamageOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Storm Drain Summary</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color={
                  stormData.status === 'Flood Risk' ? 'error' : stormData.status === 'Elevated' ? 'warning.main' : 'success.main'
                }>
                  {stormData.status}
                </Typography>
                <Typography variant="caption" color="textSecondary">Status</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {`${stormData.currentLevelCm} cm`}
                </Typography>
                <Typography variant="caption" color="textSecondary">Current Level</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="info.main">
                  {new Date(stormData.nextInspectionAt).toLocaleTimeString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">Next Inspection</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Level Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <WavesOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Water Level (7 Days)</Typography>
          </Box>
          <Box
            ref={chartRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default StormDrainEntityDrawer;


